import validateFieldForPlaceholder from 'lib/validateFieldPlaceholder';
import React from 'react';

import styles from './styles.module.css';
import { StructuredDataObject, StructuredDataValue } from './types';

interface Props {
  value?: StructuredDataValue;
  renderString?: (value: string) => React.ReactNode;
}

const isStructuredDataObject = (
  value: StructuredDataValue | undefined,
): value is StructuredDataObject =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const isEmptyStructuredDataValue = (
  value?: StructuredDataValue,
): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (isStructuredDataObject(value)) return Object.keys(value).length === 0;
  return false;
};

const STRUCTURED_DATA_INITIALISMS: Record<string, string> = {
  Api: 'API',
  Aws: 'AWS',
  Cli: 'CLI',
  Hcl: 'HCL',
  Id: 'ID',
  Url: 'URL',
  Yaml: 'YAML',
};

export const humanizeStructuredDataKey = (key: string): string =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase())
    .replace(/\bIa C\b/g, 'IaC')
    .split(' ')
    .map((word) => STRUCTURED_DATA_INITIALISMS[word] || word)
    .join(' ');

const RenderStructuredData: React.FC<Props> = ({ value, renderString }) => {
  if (isEmptyStructuredDataValue(value)) {
    return <span>{validateFieldForPlaceholder(null)}</span>;
  }

  if (Array.isArray(value)) {
    return (
      <ol className={styles.structuredDataList}>
        {value.map((item, index) => (
          <li className={styles.structuredDataListItem} key={index}>
            <RenderStructuredData value={item} renderString={renderString} />
          </li>
        ))}
      </ol>
    );
  }

  if (isStructuredDataObject(value)) {
    const sortedKeys = Object.keys(value).sort((left, right) =>
      left.toLowerCase().localeCompare(right.toLowerCase()),
    );

    return (
      <table className={styles.structuredDataTable}>
        <tbody>
          {sortedKeys.map((key) => (
            <tr key={key}>
              <td className={styles.structuredDataLabel}>
                {humanizeStructuredDataKey(key)}
              </td>
              <td className={styles.structuredDataValue}>
                <RenderStructuredData
                  value={value[key]}
                  renderString={renderString}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (typeof value === 'string' && renderString) {
    return (
      <div className={styles.structuredDataMarkdown}>{renderString(value)}</div>
    );
  }

  return <span className={styles.structuredDataScalar}>{String(value)}</span>;
};

export default RenderStructuredData;
